import { paths } from '@/app/routes';
import React from 'react';
import { useCallback, useContext, useMemo, useState, createContext } from 'react';
import { applyColumnOrder } from '@/components/table/columnOrder';

export const DEFAULT_HIDDEN_COLUMNS = ['invoiced', 'closed-lost', 'endorsement-in-progress'];
const HIDDEN_COLUMNS_KEY = 'hidden-columns';
const COLUMN_WIDTHS_KEY = 'column-widths';
const COLUMN_ORDER_KEY = 'column-order';

const saveToLocalStorage = ({ value, tab, array = [], variant = 'kanban' }) => {
	const key = `${variant}-${HIDDEN_COLUMNS_KEY}`;
	const saved = localStorage.getItem(key);
	let parsed = {};
	try {
		if (saved) parsed = JSON.parse(saved);
	} catch (e) {
		console.warn(
			`Error getting local storage value value=${value} tab=${tab} variant=${variant}`,
			e,
		);
	}

	const next = tab
		? { ...parsed, [value]: { ...(parsed?.[value] ?? {}), [tab]: array } }
		: { ...parsed, [value]: array };

	localStorage.setItem(key, JSON.stringify(next));
};

const getSavedColumns = ({ value, tab, variant = 'kanban', defaultHiddenColumns }) => {
	const saved = localStorage.getItem(`${variant}-${HIDDEN_COLUMNS_KEY}`);
	if (!saved) return defaultHiddenColumns;
	try {
		const parsed = JSON.parse(saved);
		return (tab ? parsed?.[value]?.[tab] : parsed?.[value]) ?? defaultHiddenColumns;
	} catch (error) {
		console.error('Error parsing saved hidden columns:', error);
		return defaultHiddenColumns;
	}
};

// Merges saved widths into a columns array. A column with a saved width has its `flex`
// dropped (width and flex can't coexist), otherwise it's returned unchanged.
export const applyColumnWidths = (columns, columnWidths = {}) =>
	columns.map((column) => {
		const savedWidth = columnWidths[column.field];
		if (savedWidth == null || column.resizable === false) return column;
		const { flex, ...rest } = column;
		return { ...rest, width: savedWidth };
	});

// We want the widths to match across like tables so we manipulate the value here
// for storing/getting from localStorage
const determineWidthValue = (value) => {
	if (value === paths.clients || value === paths.prospects) {
		return 'clients';
	} else if (
		[
			process.env.REACT_APP_PIPELINE_NEW_BUSINESS,
			process.env.REACT_APP_PIPELINE_RENEWALS,
			process.env.REACT_APP_PIPELINE_ENDORSEMENTS,
			process.env.REACT_APP_PIPELINE_CLAIMS,
			process.env.REACT_APP_PIPELINE_DEBTORS,
		].includes(value)
	) {
		return 'deals';
	}
	return value;
};

// Widths are keyed by variant + value (like hiddenColumns, minus tab) — e.g. a deal table's
// widths are scoped per its `value` (pipelineId, 'clientDeals', etc.), so views that pass
// different values naturally get independent widths, while views sharing a value share them.
const saveWidthsToLocalStorage = ({ value, widths = {}, variant = 'kanban' }) => {
	const key = `${variant}-${COLUMN_WIDTHS_KEY}`;
	const saved = localStorage.getItem(key);
	value = determineWidthValue(value);
	let parsed = {};
	try {
		if (saved) parsed = JSON.parse(saved);
	} catch (e) {
		console.warn(`Error getting local storage value value=${value} variant=${variant}`, e);
	}

	localStorage.setItem(key, JSON.stringify({ ...parsed, [value]: widths }));
};

const getSavedWidths = ({ value, variant = 'kanban' }) => {
	const saved = localStorage.getItem(`${variant}-${COLUMN_WIDTHS_KEY}`);
	if (!saved) return {};
	value = determineWidthValue(value);
	try {
		return JSON.parse(saved)?.[value] ?? {};
	} catch (error) {
		console.error('Error parsing saved column widths:', error);
		return {};
	}
};

const saveOrderToLocalStorage = ({ value, order = [], variant = 'kanban' }) => {
	const key = `${variant}-${COLUMN_ORDER_KEY}`;
	const saved = localStorage.getItem(key);
	let parsed = {};
	try {
		if (saved) parsed = JSON.parse(saved);
	} catch (e) {
		console.warn(`Error getting local storage value value=${value} variant=${variant}`, e);
	}

	localStorage.setItem(key, JSON.stringify({ ...parsed, [value]: order }));
};

const getSavedOrder = ({ value, variant = 'kanban' }) => {
	const saved = localStorage.getItem(`${variant}-${COLUMN_ORDER_KEY}`);
	if (!saved) return [];
	try {
		const order = JSON.parse(saved)?.[value];
		return Array.isArray(order) ? order : [];
	} catch (error) {
		console.error('Error parsing saved column order:', error);
		return [];
	}
};

const initialState = {
	hiddenColumns: [],
	visibleColumns: [],
	toggleColumn: (columnId) => {},
	showAllColumns: () => {},
	hideColumns: (columnIds) => {},
	columnWidths: {},
	setColumnWidth: (field, width) => {},
	columnOrder: [],
	replaceColumnOrder: (columns, order) => {},
	resetOrder: () => {},
};

const ColumnControlContext = createContext(initialState);

export const useColumnControl = () => {
	const context = useContext(ColumnControlContext);
	if (!context) {
		throw new Error('useColumnControl must be used within a ColumnControlContextProvider');
	}
	return context;
};

// Props to spread onto a DataGrid to wire it up to persisted column visibility/widths/order, e.g.
// <DataGrid {...useDataGridColumnProps(columns)} rows={rows} ... />
// Pass disableColumnWidth/disableColumnVisibility/disableColumnOrder to opt out of that piece
// (e.g. for a grid embedded in a fixed/compact layout that shouldn't persist column changes).
export const useDataGridColumnProps = (
	columns,
	{
		disableColumnWidth = false,
		disableColumnVisibility = false,
		disableColumnOrder = false,
	} = {},
) => {
	const { hiddenColumns, hideColumns, columnWidths, setColumnWidth, columnOrder } =
		useColumnControl();

	const columnVisibilityModel = useMemo(
		() =>
			columns.reduce((acc, column) => {
				acc[column.field] =
					column.hideable === false || !hiddenColumns.includes(column.field);
				return acc;
			}, {}),
		[columns, hiddenColumns],
	);

	const onColumnVisibilityModelChange = useCallback(
		(newModel) =>
			hideColumns(
				columns
					.filter((column) => newModel[column.field] === false)
					.map((column) => column.field),
			),
		[columns, hideColumns],
	);

	const columnsWithWidths = useMemo(
		() => (disableColumnWidth ? columns : applyColumnWidths(columns, columnWidths)),
		[columns, columnWidths, disableColumnWidth],
	);

	const orderedColumns = useMemo(
		() =>
			disableColumnOrder
				? columnsWithWidths
				: applyColumnOrder(columnsWithWidths, columnOrder),
		[columnOrder, columnsWithWidths, disableColumnOrder],
	);

	const onColumnWidthChange = useCallback(
		(params) => setColumnWidth(params.colDef.field, params.width),
		[setColumnWidth],
	);

	return {
		columns: orderedColumns,
		...(!disableColumnVisibility && { columnVisibilityModel, onColumnVisibilityModelChange }),
		...(!disableColumnWidth && { onColumnWidthChange }),
	};
};

export const ColumnControlContextProvider = ({
	value = '', // This can be url slug, pipelineId, etc.
	tab = '',
	variant = 'kanban',
	defaultHiddenColumns = [],
	children,
}) => {
	// State for hidden columns
	const [hiddenColumns, setHiddenColumns] = useState(() =>
		getSavedColumns({ value, tab, variant, defaultHiddenColumns }),
	);

	// State for column widths
	const [columnWidths, setColumnWidths] = useState(() => getSavedWidths({ value, variant }));

	// State for column order (keyed by variant + raw value, without the width grouping; not tab-scoped)
	const [columnOrder, setColumnOrder] = useState(() => getSavedOrder({ value, variant }));

	// Reload saved state when the provider is reused for a different table
	const storageKey = `${variant}|${value}|${tab}`;
	const [loadedStorageKey, setLoadedStorageKey] = useState(storageKey);
	if (loadedStorageKey !== storageKey) {
		setLoadedStorageKey(storageKey);
		setHiddenColumns(getSavedColumns({ value, tab, variant, defaultHiddenColumns }));
		setColumnWidths(getSavedWidths({ value, variant }));
		setColumnOrder(getSavedOrder({ value, variant }));
	}

	const setColumnWidth = useCallback(
		(field, width) => {
			setColumnWidths((prev) => {
				const next = { ...prev, [field]: width };
				saveWidthsToLocalStorage({ value, widths: next, variant });
				return next;
			});
		},
		[value, variant],
	);

	const saveColumns = useCallback(
		(array = []) => {
			saveToLocalStorage({ value, tab, array, variant });
		},
		[variant, tab, value],
	);

	const replaceColumnOrder = useCallback(
		(columns, order) => {
			setColumnOrder((prev) => {
				const next = applyColumnOrder(columns, order).map((column) => column.field);
				if (
					next.length === prev.length &&
					next.every((field, index) => field === prev[index])
				) {
					return prev;
				}
				saveOrderToLocalStorage({ value, order: next, variant });
				return next;
			});
		},
		[value, variant],
	);

	const resetOrder = useCallback(() => {
		saveOrderToLocalStorage({ value, order: [], variant });
		setColumnOrder([]);
	}, [value, variant]);

	// Toggle a single column
	const toggleColumn = useCallback(
		(columnId) => {
			setHiddenColumns((prev) => {
				const newHidden = prev.includes(columnId)
					? prev.filter((id) => id !== columnId)
					: [...prev, columnId];
				saveColumns(newHidden);
				return newHidden;
			});
		},
		[setHiddenColumns, saveColumns],
	);

	// Show all columns
	const showAllColumns = useCallback(() => {
		setHiddenColumns([]);
		saveColumns([]);
	}, [setHiddenColumns, saveColumns]);

	// Hide multiple columns
	const hideColumns = useCallback(
		(columnIds = []) => {
			setHiddenColumns(columnIds);
			saveColumns(columnIds);
		},
		[setHiddenColumns, saveColumns],
	);

	const context = useMemo(
		() => ({
			hiddenColumns,
			toggleColumn,
			showAllColumns,
			hideColumns,
			columnWidths,
			setColumnWidth,
			columnOrder,
			replaceColumnOrder,
			resetOrder,
		}),
		[
			hiddenColumns,
			toggleColumn,
			showAllColumns,
			hideColumns,
			columnWidths,
			setColumnWidth,
			columnOrder,
			replaceColumnOrder,
			resetOrder,
		],
	);

	return <ColumnControlContext.Provider value={context}>{children}</ColumnControlContext.Provider>;
};

ColumnControlContextProvider.displayName = 'ColumnControlContextProvider';
